//React
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

//Mantine
import {
  Alert,
  Card,
  Flex,
  Stepper,
  Text,
  Button,
  useComputedColorScheme,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

//Interfaces
import {
  BulkClaimForm,
  Claim,
  Healthcard,
  IndividualBulkClaimForm,
  Patient,
  Provider,
} from 'interfaces';

//Components
import { IconEye, IconEyeClosed } from '@tabler/icons-react';
import { PatientClaimHistoryColumns } from 'columns/PatientClaimHistory';
import {
  AllKeys,
  ClaimAdditionalInfoSection,
  ClaimFormProvider,
  ClaimInfoSection,
  ClaimPatientHistoryTable,
  ClaimPatientSection,
  ClaimProviderSection,
  ClaimTariffSection,
  useActiveForm,
  ClaimHealthcardSection,
  ClaimCommentSection,
  claimCommentSectionKeys,
  SharedDataSection,
  ClaimModalPreviousButton,
  ClaimModalSubmitButton,
  SKClaimDetailSectionKeys,
} from 'pages/claims/components';
import { v4 as uuidv4 } from 'uuid';

import { MRT_TableInstance } from 'mantine-react-table';
import { useClaimDraftStore } from 'store/ClaimDraftStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';
import { stripNested } from 'utils/miscUtils';
import * as Sentry from '@sentry/react';
import { formatPatientName } from '@billing/validation';
import { isFutureDate } from 'utils/dateParsing';
import { useIsSKClaim } from 'hooks/useIsSKClaim';
import { useTranslation } from '@refinedev/core';

interface IndividualClaimSectionProps {
  table: MRT_TableInstance<Claim>;
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  bulkForm: UseFormReturnType<Partial<BulkClaimForm>>;
  editComponentsMap: Record<string, React.ReactNode>;
  infoSectionKeys: AllKeys[];
  detailSectionKeys: AllKeys[];
  patientSectionKeys: AllKeys[];
  tariffSectionKeys: AllKeys[];
  providerSectionKeys: AllKeys[];
  providers: Provider[];
  healthcardSectionKeys: AllKeys[];
  handlePatientPrev: () => void;
  handlePatientNext: () => void;
  setSubmitClaims: (value: boolean) => void;
  editingFromPreview?: boolean; // are we editing from the preview step
  onFinishEditFromPreview?: () => void; // callback when finishing edit from preview
  onEditShared: () => void; // jump directly to shared step
  showErrors: boolean;
  setShowErrors: Dispatch<SetStateAction<boolean>>;
  onCancel: () => void;
  handleSharedNext: () => void;
  sharedIndex: number;
  sharedDetailSectionKeys: AllKeys[];
  additionalInfoKeys: AllKeys[];
  sharedTariffSectionKeys: AllKeys[];
  totalClaims: number;
  onStepperNavigation: () => void; // merge shared data into claims without navigation
  validateSharedBeforeNavigation: () => boolean; // validate shared data before navigating away
}

export const IndividualClaimSection: React.FC<IndividualClaimSectionProps> = ({
  table,
  currentStep,
  setCurrentStep,
  editComponentsMap,
  infoSectionKeys,
  detailSectionKeys,
  patientSectionKeys,
  tariffSectionKeys,
  providerSectionKeys,
  providers,
  healthcardSectionKeys,
  handlePatientPrev,
  handlePatientNext,
  setSubmitClaims,
  editingFromPreview = false,
  onFinishEditFromPreview,
  showErrors,
  setShowErrors,
  onEditShared,
  onCancel,
  handleSharedNext,
  sharedIndex,
  sharedDetailSectionKeys,
  additionalInfoKeys,
  sharedTariffSectionKeys,
  totalClaims,
  onStepperNavigation,
  validateSharedBeforeNavigation,
}) => {
  const computedColorScheme = useComputedColorScheme('dark');
  const { translate } = useTranslation();
  const { form, prefix } = useActiveForm();
  const isSKClaim = useIsSKClaim(form, prefix);
  const claims = form?.getValues()?.claims || [];
  const claim = claims[currentStep] || {};
  const { saveBulkDraftFromRows } = useClaimDraftStore();
  const { organization, timezone } = useCurrentSiteStore();
  const { id: currentUserID } = useCurrentUserStore();
  const hasFutureServiceStart = isFutureDate(claim.serviceStart, timezone);
  const claimDetailSectionKeys = useMemo(
    () => [
      ...detailSectionKeys,
      ...(isSKClaim ? SKClaimDetailSectionKeys : []),
    ],
    [detailSectionKeys, isSKClaim]
  );

  const inputRef = useRef<HTMLInputElement>(null); // used to focus the first input of the form (Service Start)
  const [isNewPatient, setIsNewPatient] = useState<boolean>(
    claim?.isNewPatient ?? false
  );
  const [showPatientHistory, setShowPatientHistory] = useState<boolean>(false);
  const newHealthcardID = uuidv4();
  const patientID = isNewPatient ? uuidv4() : claim.patient?.id;

  const newPatient: Patient = {
    id: patientID,
    firstName: claim.patient?.firstName || '',
    lastName: claim.patient?.lastName || '',
    address: claim.patient?.address || '',
    city: claim.patient?.city || '',
    country: claim.patient?.country || '',
    dateOfBirth: claim.patient?.dateOfBirth || null,
    maritalStatus: claim.patient?.maritalStatus || '',
    postalCode: claim.patient?.postalCode || '',
    province: claim.patient?.province || null,
    sex: claim.patient?.sex || null,
    email: null,
    phone: null,
    searchVector: null,
    healthcardID: claim.patient?.healthcardID || newHealthcardID,
    healthcards: claim.patient?.healthcards || [],
    organization,
    organizationID: organization.id,
  };
  const newHealthcard: Healthcard = {
    number: claim.healthcard?.number || '',
    registration: claim.healthcard?.registration || '',
    province: claim.healthcard?.province || '',
    id: claim.patient?.healthcardID || newHealthcardID,
    patientID,
  };

  useEffect(() => {
    if (isNewPatient) {
      form.setFieldValue(`claims.${currentStep}.patient`, newPatient);
      form.setFieldValue(`claims.${currentStep}.patientID`, patientID);
      form.setFieldValue(`claims.${currentStep}.healthcard`, newHealthcard);
      form.setFieldValue(`claims.${currentStep}.healthcardID`, newHealthcardID);
    }
  }, [currentStep, isNewPatient]);

  const handleNextClick = () => {
    const { errors } = form.validate();
    const claimPath = `claims.${currentStep}`;
    const hasClaimErrors = Object.keys(errors).some(
      (path) => path === claimPath || path.startsWith(`${claimPath}.`)
    );
    if (hasClaimErrors) {
      setShowErrors(true);
      return;
    }

    setShowErrors(false);
    setIsNewPatient(false);
    // Deep clone via structuredClone to avoid later mutation issues
    const values = form.getValues();
    Sentry.captureMessage('[BULK_AUDIT]Draft Save Request', {
      level: 'info',
      extra: {
        formType: 'bulk',
        step: currentStep,
        total: (values.claims || []).length,
        claims: (values.claims || []).map((c) => stripNested(c)),
        user: currentUserID,
      },
    });
    saveBulkDraftFromRows(
      values.shared,
      JSON.parse(JSON.stringify(values.claims || []))
    );
    handlePatientNext();
  };

  const handlePreviousClick = () => {
    setShowErrors(false);
    handlePatientPrev();
  };

  // Focus the first input field when the modal opens or when the step changes
  useEffect(() => {
    const focusFirstSection = () => {
      const el = inputRef?.current;
      if (!el) return;
      requestAnimationFrame(() => {
        el.focus();
      });

      // Scroll to top of modal
      const modalTitle = document.querySelector(
        '[className="mantine-Modal-title"]'
      ) as HTMLElement | null;
      if (!modalTitle) return;
      modalTitle.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Use two requestAnimationFrame calls to ensure the element is rendered before focusing
    const id1 = requestAnimationFrame(() => {
      requestAnimationFrame(focusFirstSection);
      return () => cancelAnimationFrame(id1);
    });
  }, [currentStep]);

  const columns = PatientClaimHistoryColumns();

  const [initialVisibleColumns] = useState<AllKeys[]>([
    'serviceStart',
    'claimNumber',
    'tariffs.icd9',
    'debits.requestedAmount',
    'debits.paidAmount',
    'debits.eobs',
    'siteID',
    'providerID',
    'tariffs',
    'claimNumber',
    'payorID',
  ]);

  // Determine the active step index for the Stepper component
  const activeVisualIndex = currentStep === -1 ? 0 : currentStep + 1; // +1 to account for shared step

  return (
    <Stepper
      active={activeVisualIndex}
      onStepClick={(stepIndex) => {
        if (stepIndex === 0) {
          setCurrentStep(-1);
          return;
        }
        // if navigating from shared (-1) directly to a claim step: merge shared first
        if (currentStep === -1 && stepIndex > 0) {
          if (!validateSharedBeforeNavigation()) return;
          onStepperNavigation();
        }
        setCurrentStep(stepIndex - 1);
      }}
      size="sm"
    >
      {/* Shared data step */}
      <Stepper.Step label="Shared" description="Shared Claim Data">
        <Card shadow="xs" p="lg" radius="md" mt="md" withBorder>
          <SharedDataSection
            index={sharedIndex}
            onCancel={onCancel}
            onNext={handleSharedNext}
            editComponentsMap={editComponentsMap}
            providers={providers}
            providerSectionKeys={providerSectionKeys}
            detailSectionKeys={sharedDetailSectionKeys}
            additionalInfoKeys={additionalInfoKeys}
            tariffSectionKeys={sharedTariffSectionKeys}
            totalClaims={totalClaims}
          />
        </Card>
      </Stepper.Step>
      {claims.map((claim: IndividualBulkClaimForm, i: number) => {
        return (
          <Stepper.Step
            key={i}
            label={`Claim ${i + 1}`}
            description={
              claim.patient
                ? formatPatientName(
                    claim.patient.firstName,
                    claim.patient.lastName
                  )
                : 'Loading ...'
            }
          >
            <Card shadow="xs" p="lg" radius="md" mt="md" withBorder>
              {showErrors && currentStep >= 0 && (
                <Alert title="Form is invalid" color="red" radius="sm" mt="md">
                  {Object.entries(form.errors)
                    .filter(([path]) => path.startsWith(`claims.${i}.`))
                    .map(([path, msg]) => {
                      const label = path.replace(`claims.${i}.`, '');
                      return <Text key={path}>{`${label}: ${msg}`}</Text>;
                    })}
                </Alert>
              )}
              <ClaimFormProvider
                form={{
                  prefix: `claims.${i}`,
                  formType: 'bulk',
                  bulkForm: form,
                }}
              >
                <ClaimProviderSection
                  title="Provider"
                  keys={providerSectionKeys}
                  cols={providerSectionKeys.length}
                  editComponentsMap={editComponentsMap}
                  providers={providers}
                  type="bulk"
                />
                <ClaimInfoSection
                  title="Claim Info"
                  keys={infoSectionKeys}
                  cols={6}
                  editComponentsMap={editComponentsMap}
                  type="bulk"
                  focusRef={i === currentStep ? inputRef : undefined}
                />
                <ClaimPatientSection
                  title="Patient"
                  keys={patientSectionKeys}
                  cols={4}
                  editComponentsMap={editComponentsMap}
                  type="bulk"
                  isNewPatient={isNewPatient}
                  setIsNewPatient={setIsNewPatient}
                  claimIndex={i}
                />
                <ClaimHealthcardSection
                  title="Healthcard"
                  keys={healthcardSectionKeys}
                  cols={3}
                  editComponentsMap={editComponentsMap}
                  type="create"
                />
                <ClaimTariffSection
                  title="Tariffs"
                  keys={tariffSectionKeys}
                  cols={1}
                  editComponentsMap={editComponentsMap}
                  type="create"
                />
                <ClaimAdditionalInfoSection
                  title="Claim Details"
                  keys={claimDetailSectionKeys}
                  cols={3}
                  editComponentsMap={editComponentsMap}
                  type="bulk"
                  initiallyOpened={false}
                />
                <ClaimCommentSection
                  title={translate('pages:claims.comments.title', 'Comments')}
                  keys={claimCommentSectionKeys}
                  cols={1}
                  editComponentsMap={editComponentsMap}
                  type="bulk"
                />
              </ClaimFormProvider>
              {!isNewPatient && claim.patient && (
                <Button
                  variant="outline"
                  radius="md"
                  mt={'xl'}
                  w={'30%'}
                  style={{ alignSelf: 'center' }}
                  color="gray"
                  onClick={() => setShowPatientHistory((prev) => !prev)}
                >
                  {showPatientHistory ? (
                    <Flex align="center" gap="sm">
                      <Text>Hide Patient Claim History</Text>
                      <IconEyeClosed />
                    </Flex>
                  ) : (
                    <Flex align="center" gap="sm">
                      <Text>View Patient Claim History</Text>
                      <IconEye />
                    </Flex>
                  )}
                </Button>
              )}
              {showPatientHistory && claim.patient && (
                <ClaimPatientHistoryTable
                  patientID={patientID}
                  columns={columns}
                  visibleColumns={initialVisibleColumns}
                  pageSize={10}
                />
              )}
            </Card>
            <Flex justify="space-between" mt="lg">
              <ClaimModalPreviousButton
                computedColorScheme={computedColorScheme}
                isLoading={false}
                handleOnClick={handlePreviousClick}
                onEditShared={onEditShared}
                onCancel={onCancel}
              />
              <ClaimModalSubmitButton
                table={table}
                computedColorScheme={computedColorScheme}
                isLoading={false}
                handleOnClick={
                  editingFromPreview ? onFinishEditFromPreview : handleNextClick
                }
                handleFinishBulkClaim={(value) =>
                  editingFromPreview && onFinishEditFromPreview
                    ? onFinishEditFromPreview()
                    : setSubmitClaims(value)
                }
                buttonText={editingFromPreview ? 'Return to Review' : 'Next'}
                disabled={hasFutureServiceStart}
                disabledReason="A claim with a future service date cannot be submitted."
              />
            </Flex>
          </Stepper.Step>
        );
      })}
    </Stepper>
  );
};
